import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'; // use WebHistory in the browser
import { DefaultLayout } from '../lib/layouts/layouts';
import StartView from '../lib/pages/StartView.vue';
import MedicalWarningView from '../lib/pages/MedicalWarningView.vue';
import Error from '../lib/pages/Error.vue';

import medicalRequestChildren from './medical-request';
import adminRequestChildren from './admin-request';
import {
  defaultMeta,
  defaultMetaAppointmentBooking,
  defaultMetaAppointmentCancellation,
  defaultMetaMessages,
  defaultMetaQuestionnaire,
  defaultNhsErrorMeta,
} from './routesSetup';
import repeatPrescriptionRequestChildren from './repeat-prescription-request';
import { toCanonicalPath } from './canonicalPaths';
import messagesChildren from './messages';
import appointmentBookingChildren from './appointment-booking';
import appointmentCancellationChildren from './appointment-cancellation';
import questionnaireChildren from './questionnaire';
import paymentChildren from './payment';

import { patchTenantAwarePush, registerTenantGuards } from './tenantRouting';
import HomeView from '../lib/pages/HomeView.vue';
import NhsLoginView from '../lib/pages/NhsLoginView.vue';
import SessionView from '../lib/pages/SessionView.vue';
import ErrorLayout from '../lib/layouts/ErrorLayout.vue';
import EmptyView from '../lib/pages/EmptyView.vue';
import NhsAppSsoView from '../lib/pages/NhsAppSsoView.vue';
import PageView from '../lib/pages/PageView.vue';
import { youHaveLoggedOut } from '@/data/pages/you-have-logged-out';

export const routes: RouteRecordRaw[] = [
  {
    path: '',
    component: HomeView,
    name: 'start-no-tenant',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'This page isn’t available',
      pageTitle: 'This page isn’t available',
      backButton: false,
    },
  },
  {
    path: '/nhs-login-callback',
    component: NhsLoginView,
    name: 'start-no-tenant-nhs-login',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Getting your data...',
      pageTitle: 'Getting your data...',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
        showBackNhsAppWeb: false,
      },
    },
  },
  {
    path: '/:section/nhs-app-callback',
    component: NhsAppSsoView,
    name: 'start-no-tenant-nhs-app-callback',
    meta: {
      requiresLoadedTenant: false,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Getting your data...',
      pageTitle: 'Getting your data...',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
        showBackNhsAppWeb: false,
      },
    },
  },
  {
    path: '/session/expired',
    name: 'session-expired',
    component: SessionView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Your session has ended',
      pageTitle: 'Your session has ended',
    },
  },
  {
    path: '/:tenantId(\\w{6})/session/expired',
    name: 'session-expired-tenant',
    component: SessionView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Your session has ended',
      pageTitle: 'Your session has ended',
    },
  },
  {
    path: '/:tenantId(\\w{6})/start',
    component: StartView,
    name: 'start-home',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'How can we help?',
      pageTitle: 'How can we help?',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
        showBackNhsAppWeb: false,
      },
    },
  },
  // NB. general 'pages' can be loaded here with content files in data/pages/
  {
    path: '/:tenantId(\\w{6})/page/you-have-logged-out',
    name: 'page-with-tenant-logout',
    component: PageView,
    meta: {
      pageTitle: youHaveLoggedOut.title,
      pageContent: youHaveLoggedOut.contentBlocks,
      backButton: youHaveLoggedOut.backButton,
      nhsAppActions: {
        showBackNhsApp: false,
      },
    },
  },
  // NB. routes for pages with content files in data/pages/
  {
    path: '/:tenantId(\\w{6})/page/:pathMatch(.*)*',
    name: 'page-with-tenant',
    component: PageView,
    meta: {
      pageTitle: 'Page',
      nhsAppActions: {
        showBackNhsApp: true,
        setBackAction: 'PP_GO_BACK',
        goToPage: 'PP_GO_BACK',
        showBackNhsAppWeb: false,
      },
    },
  },
  {
    path: '/page/:pathMatch(.*)*',
    name: 'page-no-tenant',
    component: PageView,
    meta: {
      pageTitle: 'Page',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: true,
        setBackAction: 'PP_GO_BACK',
        goToPage: 'PP_GO_BACK',
        showBackNhsAppWeb: false,
      },
    },
  },
  {
    path: '/:tenantId(\\w{6})/error',
    component: Error,
    name: 'tenant-error',
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: 'Error',
      pageTitle: 'Error',
      backButton: false,
      ...defaultNhsErrorMeta,
    },
  },
  {
    path: '/:tenantId(\\w{6})/error/nhs',
    name: 'error-tenant-present-nhs',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: '404',
      pageTitle: '404',
      backButton: false,
      ...defaultNhsErrorMeta,
    },
  },
  {
    path: '/:tenantId(\\w{6})/error/400',
    name: 'error-400-tenant-present',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: '404',
      pageTitle: '404',
      backButton: false,
      ...defaultNhsErrorMeta,
    },
  },
  {
    path: '/error/400',
    name: 'error-400',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: '404',
      pageTitle: '404',
      backButton: false,
      ...defaultNhsErrorMeta,
    },
  },
  {
    path: '/error',
    name: 'error-main',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: 'Error',
      pageTitle: 'Error',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: true,
        showBackNhsAppWeb: false,
        goToPage: 'GO_BACK',
        setBackAction: 'GO_BACK',
      },
    },
  },
  {
    path: '/:tenantId(\\w{6})',
    component: StartView,
    name: 'start',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'How can we help?',
      pageTitle: 'How can we help?',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
        showBackNhsAppWeb: false,
      },
    },
  },
  // Medical action routes
  {
    path: '/:tenantId(\\w{6})/medical-request',
    name: 'medical-request-wr',
    component: MedicalWarningView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: "Check it's not an emergency",
      pageTitle: "Check it's not an emergency",
      requiresLoadedTenant: true,
      nhsAppActions: {
        setBackAction: 'GO_BACK',
        goToPage: 'GO_BACK',
        showBackNhsApp: true,
        showBackNhsAppWeb: false,
      },
    },
    children: medicalRequestChildren,
  },
  // Admin request routes
  {
    path: '/:tenantId(\\w{6})/admin-request',
    name: 'admin-request-wt',
    component: MedicalWarningView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Check it’s not an emergency',
      pageTitle: 'Check it’s not an emergency',
      requiresLoadedTenant: true,
      nhsAppActions: {
        setBackAction: 'GO_BACK',
        goToPage: 'GO_BACK',
        showBackNhsApp: true,
        showBackNhsAppWeb: false,
      },
    },
    children: adminRequestChildren,
  },
  // Repeat prescription routes
  {
    path: '/:tenantId(\\w{6})/repeat-prescription-request',
    name: 'repeat-prescription-request-wt',
    component: EmptyView,
    // NB. ENG-20175. No content on this page.
    redirect: {
      name: 'start-home',
    },
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Repeat Prescription Request',
      pageTitle: 'Repeat Prescription Request',
    },
    children: repeatPrescriptionRequestChildren,
  },
  // ENG-21797 SMS short links. They stay live because they are already in
  // patients' inboxes, and replace rather than push so Back doesn't bounce.
  {
    path: '/:tenantId(\\w{6})/m/:messageId/:action(authenticate|reply)?',
    name: 'message-short-link',
    redirect: (to) => ({
      path: toCanonicalPath(to.path) ?? `/${to.params.tenantId}/error/400`,
      query: to.query,
      replace: true,
    }),
  },
  {
    path: '/:tenantId(\\w{6})/c/:appointmentId',
    name: 'appointment-cancellation-short-link',
    redirect: (to) => ({
      path: toCanonicalPath(to.path) ?? `/${to.params.tenantId}/error/400`,
      query: to.query,
      replace: true,
    }),
  },
  {
    path: '/:tenantId(\\w{6})/q/:requestId',
    name: 'questionnaire-short-link',
    redirect: (to) => ({
      path: toCanonicalPath(to.path) ?? `/${to.params.tenantId}/error/400`,
      query: to.query,
      replace: true,
    }),
  },
  // ENG-21800 The booking links V1 sends out. They stay live because they are
  // already in patients' inboxes, and both kinds resolve to the same route.
  {
    path: '/:tenantId(\\w{6})/booking/patient/:bookingLinkId',
    name: 'appointment-booking-patient-short-link',
    redirect: (to) => ({
      path: toCanonicalPath(to.path) ?? `/${to.params.tenantId}/error/400`,
      query: to.query,
      replace: true,
    }),
  },
  {
    path: '/:tenantId(\\w{6})/booking/public/:bookingLinkId',
    name: 'appointment-booking-public-short-link',
    redirect: (to) => ({
      path: toCanonicalPath(to.path) ?? `/${to.params.tenantId}/error/400`,
      query: to.query,
      replace: true,
    }),
  },
  // Comms routes
  {
    path: '/:tenantId(\\w{6})/messages/:messageId',
    name: 'messages',
    component: EmptyView,
    // ENG-21791 land on the thread; the guard asks the endpoint and challenges only if needed
    redirect: (to) => ({
      name: 'messages-message',
      params: to.params,
      query: to.query,
    }),
    meta: {
      ...defaultMetaMessages,
    },
    children: messagesChildren,
  },
  // Appointment booking routes
  {
    path: '/:tenantId(\\w{6})/appointment-booking/:bookingLinkId',
    name: 'appointment-booking',
    component: EmptyView,
    redirect: (to) => ({
      name: 'appointment-booking-patient-details',
      params: to.params,
      query: to.query,
    }),
    meta: {
      ...defaultMetaAppointmentBooking,
    },
    children: appointmentBookingChildren,
  },
  // Questionnaire routes
  {
    path: '/:tenantId(\\w{6})/questionnaire/:requestId',
    name: 'questionnaire',
    component: EmptyView,
    redirect: (to) => ({
      name: 'questionnaire-form',
      params: to.params,
      query: to.query,
    }),
    meta: {
      ...defaultMetaQuestionnaire,
    },
    children: questionnaireChildren,
  },
  // Appointment cancellation routes
  {
    path: '/:tenantId(\\w{6})/appointment-cancellation/:appointmentId',
    component: EmptyView,
    meta: {
      ...defaultMetaAppointmentCancellation,
    },
    children: appointmentCancellationChildren,
  },
  // Invoice payment routes: reached only via a direct link containing the invoice id
  // (no discovery tile on the portal landing page, no back button anywhere in this tree).
  {
    path: '/:tenantId(\\w{6})/payment/:invoiceId',
    component: EmptyView,
    name: 'payment-wt',
    children: paymentChildren,
  },
  {
    path: '/:tenantId(\\w{6})/:pathMatch(.*)*',
    name: 'tenant-bad-request',
    redirect: (to) => `/${to.params.tenantId}/error/400`,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'bad-request',
    redirect: '/error/400',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0, left: 0 };
  },
});

// Route guards & path prefixing with tenantId
patchTenantAwarePush(router);
registerTenantGuards(router);
